import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/badge';
import { BADGE_VARIANT_GRAY_DISABLED } from '@components/badge/src/badge.type';
import { SeatingItem } from '@domain/floorplan';
import { formatSeatingLabel } from '@utils/format-seating-label/format-seating-label';

export interface AdmissionCardSeatingItemsProps {
  seatingItems: Pick<SeatingItem, 'id' | 'label'>[];
}

const MAX_VISIBLE_BEFORE_OVERFLOW = 12;
const COLLAPSED_VISIBLE_COUNT = MAX_VISIBLE_BEFORE_OVERFLOW - 1;

export const AdmissionCardSeatingItems = ({ seatingItems }: AdmissionCardSeatingItemsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const sortedSeatingItems = useMemo(
    () =>
      [...seatingItems]
        .map((item) => ({ ...item, formatted: formatSeatingLabel(item.label) }))
        .sort((a, b) => a.formatted.localeCompare(b.formatted, undefined, { numeric: true })),
    [seatingItems]
  );

  const overflow = sortedSeatingItems.length > MAX_VISIBLE_BEFORE_OVERFLOW;
  const visibleSeatingItems =
    !overflow || expanded ? sortedSeatingItems : sortedSeatingItems.slice(0, COLLAPSED_VISIBLE_COUNT);
  const hiddenCount = sortedSeatingItems.length - visibleSeatingItems.length;

  return (
    <div data-testid='admission-card--seating-items' className='grid grid-cols-4 gap-2'>
      {visibleSeatingItems.map((item) => (
        <Badge key={item.id} variant={BADGE_VARIANT_GRAY_DISABLED} squared className='w-full justify-center px-3 py-1'>
          {item.formatted}
        </Badge>
      ))}
      {overflow && !expanded && (
        <Badge
          variant={BADGE_VARIANT_GRAY_DISABLED}
          squared
          className='w-full justify-center px-3 py-1'
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          ariaExpanded={false}
          dataTestId='admission-card--seating-items-show-more'>
          {t('floorplanV2:seatingItemsMoreCount', { count: hiddenCount })}
        </Badge>
      )}
      {overflow && expanded && (
        <Badge
          variant={BADGE_VARIANT_GRAY_DISABLED}
          squared
          className='w-full justify-center px-3 py-1'
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          ariaExpanded={true}
          dataTestId='admission-card--seating-items-show-less'>
          {t('common:viewLess')}
        </Badge>
      )}
    </div>
  );
};
